import LegalPageLayout from "@/components/LegalPageLayout";
import SEO from "@/components/SEO";

export default function TermsOfService() {
  return (
    <LegalPageLayout
      eyebrow="Legal"
      title="Terms of Service"
      updated="17 August 2026"
    >
      <SEO
        title="Terms of Service | Checkout DM"
        description="The terms governing merchant and customer use of Checkout DM's Instagram DM checkout, payments, and fulfillment platform."
        path="/terms-of-service"
      />
      <p>
        These Terms of Service ("Terms") govern access to and use of
        CheckoutDM ("CheckoutDM," "we," "us," "our"), a service operated by
        M/s Healthy Celeb, that enables businesses ("Merchants") to automate
        product sales and order collection through Instagram Direct
        Messages. By registering for or using CheckoutDM, you ("Merchant,"
        "you") agree to be bound by these Terms.
      </p>

      <h2>1. Description of Service</h2>
      <p>
        CheckoutDM connects to a Merchant's Instagram Business Account via
        the Instagram Graph API and automates keyword-triggered conversations
        with the Merchant's customers, including product selection,
        order-detail collection, and generation of payment links or payment
        instructions. CheckoutDM is a technology facilitator; it is not a
        party to the sale of goods between Merchants and their Customers.
      </p>

      <h2>2. Eligibility</h2>
      <ul>
        <li>You must be at least 18 years old and legally capable of entering into a binding contract to use CheckoutDM.</li>
        <li>You must own or have proper authorization to connect the Instagram Business Account you configure with CheckoutDM.</li>
        <li>
          You are responsible for ensuring your use of CheckoutDM complies
          with Meta's{" "}
          <a href="https://developers.facebook.com/devpolicy/" target="_blank" rel="noopener noreferrer">
            Platform Terms
          </a>{" "}
          and{" "}
          <a href="https://help.instagram.com/581066165581870" target="_blank" rel="noopener noreferrer">
            Instagram's Terms of Use
          </a>
          .
        </li>
      </ul>

      <h2>3. Merchant Responsibilities</h2>
      <ul>
        <li>You are solely responsible for the accuracy of your product listings, pricing, and fulfillment of orders placed through CheckoutDM.</li>
        <li>You are responsible for maintaining valid, working payment credentials (e.g. Payment Gateway account such as Razorpay, UPI ID) to receive payments from Customers.</li>
        <li>You agree not to use CheckoutDM to sell illegal, prohibited, or Meta-restricted goods or services.</li>
        <li>You agree not to use CheckoutDM to send unsolicited, spam, or deceptive messages to Instagram users.</li>
        <li>You are responsible for honoring applicable consumer protection, refund, and data protection laws in your jurisdiction.</li>
      </ul>

      <h2>4. Payments</h2>
      <p>
        Payments between Customers and Merchants are processed through
        third-party payment providers, including Razorpay, Stripe (coming
        soon), or via UPI/QR code instructions configured by the Merchant.
        CheckoutDM does not hold, custody, or guarantee any funds. For
        UPI/QR-based payment methods, order confirmation and payment
        verification may require manual confirmation by the Merchant, as
        these methods do not provide automated payment verification.
        CheckoutDM is not responsible for payment disputes, chargebacks,
        refunds, or failed transactions between Merchants and Customers;
        these are the responsibility of the Merchant and the applicable
        payment provider.
      </p>

      <h2>5. Subscription and Fees</h2>
      <p>
        Access to CheckoutDM may require payment of subscription fees as
        described at the time of signup. Fees are billed monthly or
        annually, as specified at the time of sign-up, and are
        non-refundable. We reserve the right to change pricing with
        reasonable prior notice.
      </p>

      <h2>6. Service Availability</h2>
      <p>
        CheckoutDM relies on third-party platforms, including Meta's
        Instagram Graph API and Razorpay's payment infrastructure, which are
        outside our control. We do not guarantee uninterrupted or
        error-free service and are not liable for outages, delays, or
        message failures caused by these third-party platforms.
      </p>
      <p>
        Message and comment delivery through Instagram is subject to
        Instagram's own technical limits, rate limits, and moderation
        systems. In particular, Instagram may temporarily throttle or delay
        messages to or from an account that receives an unusually high
        volume of comments or direct messages in a short period (for
        example, due to a viral post), and such throttling may affect an
        individual Merchant's account or, in some cases, message delivery
        more broadly. Instagram's messaging window policies also
        independently restrict when messages can be sent to a given
        Customer, regardless of CheckoutDM's own performance. CheckoutDM is
        not liable for delayed or failed message delivery, or resulting
        lost sales or orders, arising from Instagram/Meta's rate limiting,
        API downtime, or policy changes.
      </p>

      <h2>7. Data and Privacy</h2>
      <p>
        Our collection and use of data in connection with CheckoutDM is
        described in our <a href="/privacy-policy">Privacy Policy</a>, which
        forms part of these Terms.
      </p>

      <h2>8. Intellectual Property</h2>
      <p>
        CheckoutDM and its underlying technology, branding, and content are
        owned by M/s Healthy Celeb. Merchants retain ownership of their own
        product content, catalog data, and customer information.
      </p>

      <h2>9. Termination</h2>
      <p>
        You may stop using CheckoutDM and disconnect your Instagram account
        at any time. We reserve the right to suspend or terminate access to
        CheckoutDM, with or without notice, if we reasonably believe these
        Terms, Meta's policies, or applicable law have been violated.
      </p>

      <h2>10. Disclaimer of Warranties</h2>
      <p>
        CheckoutDM is provided "as is" and "as available," without
        warranties of any kind, express or implied, including fitness for a
        particular purpose or non-infringement, to the maximum extent
        permitted by law.
      </p>

      <h2>11. Limitation of Liability</h2>
      <p>
        To the maximum extent permitted by law, M/s Healthy Celeb shall not
        be liable for any indirect, incidental, special, or consequential
        damages, including loss of profits or data, arising from your use of
        CheckoutDM.
      </p>

      <h2>12. Governing Law</h2>
      <p>
        These Terms are governed by the laws of India, and any disputes
        shall be subject to the exclusive jurisdiction of the courts of
        Panchkula, Haryana.
      </p>

      <h2>13. Changes to These Terms</h2>
      <p>
        We may update these Terms from time to time. Material changes will
        be reflected by updating the "Last updated" date above. Continued
        use of CheckoutDM after changes constitutes acceptance of the
        revised Terms.
      </p>

      <h2>14. Contact Us</h2>
      <p>
        <a href="mailto:support@checkoutdm.com">support@checkoutdm.com</a>
        <br />
        #989, FF, Sector - 11, Panchkula, Haryana, India - 134112
      </p>
    </LegalPageLayout>
  );
}
